import React, { useState, useEffect, useContext, Fragment } from 'react';
import useAxios from '../../hooks/useAxios';
import AuthContext from '../../contexts/auth-context';
import { urls } from '../config/variables';
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Divider,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchableSelect from '../ui/SearchableSelect/SearchableSelect';

const defaultFilters = {
  fromDate: '',
  toDate: '',
  department: '',
  division: '',
  doctor: '',
  patient: '',
  status: '',
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 520 },
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: 24,
  p: 0,
  overflow: 'hidden',
};

const headerStyle = {
  px: 3,
  py: 2.5,
  background: '#4abba7',
  color: 'common.white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const contentStyle = {
  px: 3,
  py: 3,
};

const FilterModal = ({ open, onClose, onApply, initialFilters = {}, newFiltersAfterDelete, fields, customFields }) => {
  const [filters, setFilters] = useState(() => ({
    ...defaultFilters,
    ...initialFilters,
  }));

  const [doctorList, srtDoctorList] = useState([])
  const [patientList, setPatientList] = useState([]);

  const statusList = [
    { value: 'Approved', label: 'Approved' },
    { value: 'Partially accepted', label: 'Partially accepted' },
    { value: 'Rejected', label: 'Rejected' },
  ]

    const { operation } = useAxios();
    const authCtx = useContext(AuthContext);
    const token = authCtx.token;

  useEffect(() => {
    setFilters({
      ...defaultFilters,
      ...initialFilters,
    });
  }, [initialFilters]);

  const handleChange = (event) => {
    // check if the event is from TextField or SearchableSelect
    const name = event.target?.name || event.name;
    const value = event.target?.value || event.value;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReset = () => {
    // reset the default filters and the custom filters if exist.
    if(Array.isArray(customFields) && customFields.length > 0) {
      const customFieldsName = customFields.map((f) => f.name)
      customFieldsName.forEach(key => {
        defaultFilters[key] = '';
      });
    }
    setFilters(defaultFilters);
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  useEffect(() => {
    handleApply()
    // eslint-disable-next-line
  }, [])

const departments = [
  { value: 'INTERNAL_MEDICINE', label: 'Internal Medicine' },
  { value: 'SURGERY', label: 'Surgery' },
  { value: 'PEDIATRICS', label: 'Pediatrics' },
  { value: 'OB_GYN', label: 'Ob Gyn' },
  { value: 'EMERGENCY', label: 'Emergency' },
  { value: 'RADIOLOGY', label: 'Radiology' },
  { value: 'PATHOLOGY', label: 'Pathology' },
  { value: 'ANESTHESIOLOGY', label: 'Anesthesiology' },
  { value: 'FAMILY_MEDICINE', label: 'Family Medicine' },
  { value: 'OTHER', label: 'Other' }
];

const divisions = [
  { value: 'OUTPATIENT', label: 'Outpatient' },
  { value: 'INPATIENT', label: 'Inpatient' },
  { value: 'CRITICAL_CARE', label: 'Critical Care' },
  { value: 'DIAGNOSTICS', label: 'Diagnostics' },
  { value: 'REHABILITATION', label: 'Rehabilitation' },
  { value: 'RESEARCH', label: 'Research' },
  { value: 'ADMINISTRATION', label: 'Administration' },
  { value: 'OTHER', label: 'Other' }
];

  const getDoctorList = () => {
    operation(
      {
        method: 'GET',
        url: urls.DOCTOR_LIST,
        headers: { Authorization: `token ${token}` },
      },
      (res) => {
        srtDoctorList(res)
      },
      (err) => console.log(err),
    );
  };

  const getPatientList = () => {
    operation(
      {
        method: 'GET',
        url: urls.PATIENT_LIST,
        headers: { Authorization: `token ${token}` },
      },
      (res) => {
        setPatientList(res)
      },
      (err) => console.log(err),
    );
  };

    useEffect(() => {
        getDoctorList();
        getPatientList()
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
      if(newFiltersAfterDelete) {
        setFilters(newFiltersAfterDelete)
      }
    }, [newFiltersAfterDelete])

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={headerStyle}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Refine Results
            </Typography>
            <Typography variant="body2">
              Tailor the view by date, department, and reporting cadence.
            </Typography>
          </Box>
          <IconButton aria-label="close filter modal" onClick={onClose} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={contentStyle}>
          {fields.includes("date")
            && <>
              <Divider textAlign="left" sx={{ mb: 3 }}>
                <Chip label="Date Range" size="small" color="primary" variant="outlined" />
              </Divider>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="fromDate"
                    label="From"
                    type="date"
                    fullWidth
                    value={filters.fromDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    helperText={!filters.fromDate ? 'Select the start date' : ' '}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="toDate"
                    label="To"
                    type="date"
                    fullWidth
                    value={filters.toDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    helperText={!filters.toDate ? 'Select the end date' : ' '}
                  />
                </Grid>
              </Grid>
            </>}

          {(fields.includes("department") || fields.includes("division") || fields.includes("doctor") || fields.includes("patient") || fields.includes("status") || (customFields && customFields.length > 0)) && <Divider textAlign="left" sx={{ mb: 3 }}>
            <Chip label="Scope" size="small" color="primary" variant="outlined" />
          </Divider>}

          <Stack spacing={2} sx={{ mb: 2 }}>
            {fields.includes("department") && 
            <SearchableSelect
                value={filters.department && departments.find((dept) => dept.value === filters.department)}
                onChange={(value) => handleChange({name: 'department', value: value.value})}
                options={departments}
                placeholder={'Select department'}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
              />
            }

            {fields.includes("division") && 
            <SearchableSelect
                value={filters.division && divisions.find((divi) => divi.value === filters.division)}
                onChange={(value) => handleChange({name: 'division', value: value.value})}
                options={divisions}
                placeholder={'Select division'}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
              />}

            {fields.includes("doctor") &&
            <SearchableSelect
                value={filters.doctor && {doctor_name: filters.doctor}}
                onChange={(value) => handleChange({name: 'doctor', value: value.doctor_name})}
                options={doctorList}
                placeholder={'Select doctor'}
                getOptionLabel={(option) => option.doctor_name}
                getOptionValue={(option) => option.doctor_name}
              />}

            {fields.includes("patient") && <SearchableSelect
                name="patient"
                value={filters.patient && {patient_name: filters.patient}}
                onChange={(value) => handleChange({name: 'patient', value: value.patient_name})}
                options={patientList}
                placeholder={'Select patient'}
                getOptionLabel={(option) => option.patient_name}
                getOptionValue={(option) => option.patient_name}
              />}

            {fields.includes("status") && 
            <SearchableSelect
                value={filters.status && {label: filters.status, value: filters.status}}
                onChange={(value) => handleChange({name: 'status', value: value.value})}
                options={statusList}
                placeholder={'Select status'}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
              />}

            {customFields && customFields.map((customField, index) => {
              return <Fragment key={index}>
                  <SearchableSelect
                  value={filters[customField.name] && customField.options.find((opt) => opt.value === filters[customField.name])}
                  onChange={(value) => handleChange({name: customField.name, value: value.value})}
                  options={customField.options}
                  placeholder={`Select ${customField.label}`}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                />
              </Fragment>
            })}

            {/* <Divider textAlign="left" sx={{ mb: 3 }}>
              <Chip label="Scope" size="small" color="primary" variant="outlined" />
            </Divider> */}
          </Stack>

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} gap={1.5} justifyContent="flex-end">
            <Button color="inherit" onClick={handleReset} sx={{ textTransform: 'none' }}>
              Reset
            </Button>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleApply} sx={{ textTransform: 'none' }}>
              Apply Filters
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default FilterModal;